/** @jsx jsx */
import { css, jsx } from '@emotion/core';
import styled from '@emotion/styled';
import React, { FormEvent, useEffect, useState } from 'react';
import { Link as ReactRouterDOMLink } from 'react-router-dom';
import Field from 'redux-form/lib/Field';
import reduxForm from 'redux-form/lib/reduxForm';
import { AnchorAPI } from '../../modules/AnchorAPI';
import { ContactabilityStatus } from '../../modules/AnchorAPI/contactability/fetchContactabilityLocationRule';
import { Button } from '../../shared/Button/NewButton';
import { LinkText } from '../../shared/Link';
import { Text } from '../../shared/Text';
import Captcha from '../Captcha';
import { FieldInputWithRedux, FieldCheckboxWithRedux } from '../Field';
import events from '../SignupPageContainer/events';
import ErrorAlert from './components/ErrorAlert';

import Box from '../../shared/Box';
import {
  EXISTING_ACCOUNT_ERROR_MESSAGE,
  EXISTING_EMAIL_ERROR_MESSAGE,
  PASSWORD_ERROR_MESSAGE,
  validate,
} from '../../user';
// @ts-ignore
import styles from '../OnboardingRouter/styles.sass';

const INPUT_FIELDS = [
  {
    name: 'name',
    placeholder: 'Full Name',
    type: 'text',
  },
  {
    name: 'email',
    placeholder: 'Email',
    type: 'email',
  },
  {
    name: 'password',
    placeholder: 'Password',
    type: 'password',
  },
];

type Props = {
  error?: string;
  feedUrl?: string;
  handleSubmit: (data: FormEvent<HTMLFormElement>) => void;
  invalid: boolean;
  onClickRequestResetPassword: () => void;
  isLoading?: boolean;
  submitting: boolean;
  isV3Captcha?: boolean;
  loginUrl?: string;
  isImporting: boolean;
  isWordPressReferral: boolean;
  change: (field: string, value: any) => void;
};

// This is currently built only for onboarding
const SignupForm = ({
  error,
  feedUrl = '',
  handleSubmit,
  submitting,
  onClickRequestResetPassword,
  isLoading = false,
  isV3Captcha = false,
  loginUrl,
  isImporting,
  isWordPressReferral,
  change,
}: Props) => {
  const [showContactCheckbox, setShowContactCheckbox] = useState(false);

  /**
   * Sets the `hasOptedIntoContactability` checkbox value
   * Hides/renders the checkbox based on user location contactability rules
   */
  useEffect(() => {
    AnchorAPI.fetchContactabilityLocationRule()
      .then(({ countryRule }) => {
        const defaultCheckboxValue =
          countryRule === ContactabilityStatus.OPTED_IN ||
          countryRule === ContactabilityStatus.OPTED_IN_SECONDARY_RESPONSE ||
          countryRule === ContactabilityStatus.NEEDS_RESPONSE_OPT_IN;
        change('hasOptedIntoContactability', defaultCheckboxValue);

        if (countryRule && countryRule !== ContactabilityStatus.OPTED_IN) {
          setShowContactCheckbox(true);
        }
      })
      .catch(err => {
        throw new Error(err.message);
      });
  }, []);

  const isSubmitDisabled = isLoading || submitting;

  const finalLoginUrl =
    loginUrl ||
    `/login${feedUrl ? `?feedUrl=${encodeURIComponent(feedUrl)}` : ''}`;

  return (
    <form
      onSubmit={data => {
        if (!isSubmitDisabled) handleSubmit(data);
      }}
      id="SignupForm"
      method="POST"
      noValidate={true}
    >
      <FieldsContainer>
        {INPUT_FIELDS.map(field => (
          <FieldWrapper key={field.name}>
            <Field
              name={field.name}
              placeholder={field.placeholder}
              component={FieldInputWithRedux}
              type={field.type}
              key={field.name}
              autofocus={field.name === 'name'}
              ariaDescribedBy={`${field.name}-error`}
            />
          </FieldWrapper>
        ))}
        <CatpchaWrapper>
          <Field
            name="captcharesponse"
            isV3={isV3Captcha}
            component={Captcha}
          />
        </CatpchaWrapper>
      </FieldsContainer>
      {error && (
        <Box display="flex" justifyContent="center">
          <Box maxWidth={404} width="100%">
            {(() => {
              switch (error) {
                case EXISTING_ACCOUNT_ERROR_MESSAGE:
                  return (
                    <ErrorAlert
                      title="Existing account found"
                      renderMessage={
                        <React.Fragment>
                          We found an existing account that uses these
                          credentials. You can{' '}
                          <ReactRouterDOMLink
                            to={finalLoginUrl}
                            onClick={() => events.signUpSignInButtonClicked()}
                          >
                            log in
                          </ReactRouterDOMLink>{' '}
                          ‌or try again with a different email address.
                        </React.Fragment>
                      }
                    />
                  );
                case EXISTING_EMAIL_ERROR_MESSAGE:
                  return (
                    <ErrorAlert
                      title="This email address is already being used"
                      renderMessage={
                        <React.Fragment>
                          We found an existing account with this email address,
                          but this password doesn’t match. You can try again or{' '}
                          <ReactRouterDOMLink
                            to={finalLoginUrl}
                            onClick={onClickRequestResetPassword}
                          >
                            reset your password
                          </ReactRouterDOMLink>
                          .
                        </React.Fragment>
                      }
                    />
                  );
                case PASSWORD_ERROR_MESSAGE:
                  return <ErrorAlert title={error} />;
                default:
                  return (
                    <ErrorAlert
                      title="Unable to create account"
                      renderMessage={
                        <React.Fragment>
                          Sorry, but we were unable to create your account.
                          Please refresh the page and try again, or visit{' '}
                          <LinkText
                            to="https://help.anchor.fm/hc/en-us"
                            isInline={true}
                          >
                            help.anchor.fm
                          </LinkText>{' '}
                          so we can help you troubleshoot.
                        </React.Fragment>
                      }
                    />
                  );
              }
            })()}
          </Box>
        </Box>
      )}
      <ButtonWrapper>
        <Button
          css={css`
            width: 100%;
          `}
          onClick={events.signUpRegisterButtonClicked}
          type="submit"
          color="purple"
          isDisabled={isSubmitDisabled}
        >
          Sign up
        </Button>
      </ButtonWrapper>
      <div className={styles.terms}>
        {showContactCheckbox && (
          <Field
            name="hasOptedIntoContactability"
            type="checkbox"
            label="Please send me news and offers from Anchor"
            cssProp={css`
              margin-bottom: 14px;
              margin-top: 17px;
            `}
            component={FieldCheckboxWithRedux}
          />
        )}
        {!isWordPressReferral && (
          <LoginImportLink
            isImporting={isImporting}
            finalLoginUrl={finalLoginUrl}
          />
        )}
        <p
          css={css`
            margin-top: 32px;
          `}
        >
          By continuing, you agree to our{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'tos' })}
            isInline={true}
            to="/tos"
            color="gray"
          >
            Terms of Service
          </LinkText>{' '}
          and{' '}
          <LinkText
            target="_blank"
            onClick={() => events.signUpLegalLinkClicked({ link: 'privacy' })}
            isInline={true}
            color="gray"
            to="/privacy"
          >
            Privacy Policy
          </LinkText>
          .
        </p>
      </div>
    </form>
  );
};

function LoginImportLink({
  isImporting,
  finalLoginUrl,
}: Pick<Props, 'isImporting'> & { finalLoginUrl: string }) {
  return isImporting ? (
    <div
      css={css`
        margin-bottom: 32px;
      `}
    >
      <Text size="md" align="center">
        Already have an account?{' '}
        <Text size="md" isBold={true} isInline={true}>
          <ReactRouterDOMLink to={finalLoginUrl}>Log in</ReactRouterDOMLink>{' '}
        </Text>
        instead.
      </Text>
    </div>
  ) : (
    <p
      css={css`
        text-align: center;
        font-size: 1.4rem;
      `}
    >
      You can also{' '}
      <ReactRouterDOMLink
        data-cy="importLink"
        to="/switch/form"
        onClick={events.signUpImportButtonClicked}
      >
        import
      </ReactRouterDOMLink>{' '}
      an existing podcast.
    </p>
  );
}

const FieldsContainer = styled.div`
  max-width: 410px;
  width: 100%;
  margin: auto;
  overflow: hidden;
`;

const FieldWrapper = styled.div`
  margin-bottom: 10px;
`;
const CatpchaWrapper = styled.div`
  margin-top: 24px;
`;

const ButtonWrapper = styled.div`
  margin: 32px auto 14px;
  max-width: 300px;
  width: 100%;
`;

export default reduxForm<
  {
    name: string;
    email: string;
    password: string;
    captcharesponse: string;
    hasOptedIntoContactability: boolean;
  },
  Props
>({
  form: 'SignupForm',
  validate,
  initialValues: {
    hasOptedIntoContactability: false,
  },
})(SignupForm);
